import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  initiateResourceDetailsDrawerThunk,
  InitiateResourceDetailsDrawerThunkArgs,
} from "@/store/resource-details/thunks/initiateResourceDetailsDrawerThunk";
import { AllTypeResourceItem } from "@/store/resources-grid/utils/types/resources";
import { getResourceExplorerSingleResourceBillingDataThunk } from "@/store/resource-explorer/thunks/resource-explorer/getResourceExplorerSingleResourceBillingDataThunk";
import { getResourceDetailsSingleResourceDataThunk } from "@/store/resource-details/thunks/data/getResourceDetailsSingleResourceDataThunk";
import {
  setResourceDetailsCoverageRelatedData,
  setResourceDetailsTargetTags,
} from "@/store/resource-details/resourceDetailsSlice";
import {
  CostType,
  DateDataPoint,
  Dates,
  GetUsersMeOrganisationsCurrentAccountsByAccountIdProvidersAndProviderResourcesApiResponse,
  ProviderType,
  ResourceType,
  Tags,
} from "@/services/cloudchipr.api";
import {
  ResourceDetailsActiveTab,
  ResourceDetailsTargetType,
} from "@/store/resource-details/utils/types/common";
import { resourceExplorerDatesSelector } from "@/store/resource-explorer/selectors/current-resource-explorer/data/resourceExplorerDatesSelector";
import { RootState } from "@/store/store";
import { isResourceKubernetes } from "@/store/resources-grid/utils/helpers/isResourceKubernetes";
import { resourceExplorerCostTypeSelector } from "@/store/resource-explorer/selectors/current-resource-explorer/data/resourceExplorerCostTypeSelector";

interface BEResourceData {
  label: string;
  dates?: Dates;
  region: string;
  service: ResourceType;
  accountId: string;
  providerOrgId?: string;
  dataPoint?: DateDataPoint;
  resourceId: string;
  filterValue: string;
  liveAccountId: string | null;
  providerAccountId: string;
  liveUsageProvider: ProviderType;
  providerAccountName: string;
  costType?: CostType;
}

export const setBillingExplorerResourceDetailsDrawerResourceDataThunk =
  createAsyncThunk(
    "resourceExplorer/setBillingExplorerResourceDetailsDrawerResourceData",
    async (args: BEResourceData, { dispatch, getState }) => {
      const { accountId, resourceId, liveUsageProvider, liveAccountId } = args;

      const state = getState() as RootState;
      const dates = resourceExplorerDatesSelector(state);
      const costType = resourceExplorerCostTypeSelector(state);

      const initiateResourceDetailsDrawerThunkArgs =
        generateResourceDetailsDrawerThunkArgsFromBEData({ ...args, costType });

      dispatch(
        initiateResourceDetailsDrawerThunk(
          initiateResourceDetailsDrawerThunkArgs,
        ),
      );
      dispatch(setResourceDetailsCoverageRelatedData({ dates }));

      let billingTags: Tags = [];
      dispatch(
        // get and apply resource tags
        getResourceExplorerSingleResourceBillingDataThunk({
          provider: liveUsageProvider,
          accountId,
          resourceId,
        }),
      )
        .unwrap()
        .then((response) => {
          billingTags = response?.tags ?? [];

          dispatch(
            setResourceDetailsTargetTags({
              loading: false,
              tags: response?.tags ?? [],
            }),
          );
        });

      dispatch(
        // get and apply single resource data
        getResourceDetailsSingleResourceDataThunk({
          provider: liveUsageProvider,
          accountId: liveAccountId,
          resourceId,
        }),
      )
        .unwrap()
        .then((response) => {
          if (!response) {
            return;
          }

          dispatch(
            initiateResourceDetailsDrawerThunk(
              generateResourceDetailsDrawerThunkArgsFromResourceData(
                response,
                billingTags,
                initiateResourceDetailsDrawerThunkArgs,
              ),
            ),
          );
        });
    },
  );

const generateResourceDetailsDrawerThunkArgsFromBEData = ({
  label,
  dates,
  region,
  service,
  costType,
  dataPoint,
  filterValue,
  liveAccountId,
  providerOrgId,
  providerAccountId,
  liveUsageProvider,
  providerAccountName,
}: BEResourceData): InitiateResourceDetailsDrawerThunkArgs => {
  const targetResource = {
    region,
    providerOrgId,
    resourceType: service,
    name: label || filterValue,
    provider: liveUsageProvider,
    accountId: liveAccountId ?? "",
    providerAccountId: providerAccountId,
    provider_unique_identifier: filterValue,
    providerAccountName: providerAccountName,
    billing_explorer_resource_identifier: [filterValue],
  };

  const resourceAdditionalDetails = {
    details: [
      { title: "Name", value: label || filterValue },
      { title: "Instance Id", value: filterValue },
      {
        title: "Account",
        value: providerAccountName || providerAccountId,
      },
      { title: "Region", value: region },
    ],
  };

  const availableTabs: ResourceDetailsActiveTab[] = [
    "general",
    "costs",
    "opportunities",
  ];

  if (["aws", "azure"].includes(liveUsageProvider)) {
    availableTabs.push("coverage");
  }

  return {
    availableTabs,
    openedFrom: "billingExplorer",
    resource: targetResource,
    tags: { loading: true, tags: [] },
    resourceAdditionalDetails,
    billingRelatedDetails: {
      dates,
      data_point: dataPoint,
      cost_type: costType,
    },
  };
};

const generateResourceDetailsDrawerThunkArgsFromResourceData = (
  resource: GetUsersMeOrganisationsCurrentAccountsByAccountIdProvidersAndProviderResourcesApiResponse,
  billingTags: Tags,
  initiateResourceDetailsDrawerThunkArgs: InitiateResourceDetailsDrawerThunkArgs,
): InitiateResourceDetailsDrawerThunkArgs => {
  const resourceTags = resource?.tags ?? [];
  const tags =
    billingTags.length > resourceTags.length ? billingTags : resourceTags;

  const availableTabs: ResourceDetailsActiveTab[] = [
    "general",
    "costs",
    "metrics",
    "opportunities",
  ];

  const provider = isResourceKubernetes(resource as AllTypeResourceItem)
    ? "kubernetes"
    : resource.account?.provider;

  if (["aws", "azure"].includes(provider)) {
    availableTabs.push("coverage");
  }

  return {
    ...initiateResourceDetailsDrawerThunkArgs,

    availableTabs,

    resource: {
      ...initiateResourceDetailsDrawerThunkArgs.resource,
      resourceType: resource.resource_type,
    } as ResourceDetailsTargetType,

    tags: { tags, loading: false },

    resourceAdditionalDetails: {
      resource: resource as unknown as AllTypeResourceItem,
    },
  };
};
